const getErrorText = (error) => {
  const data = error?.response?.data;

  return [
    data?.message,
    data?.error,
    data?.error_msg,
    data?.detail,
    data?.error_code,
    typeof data === 'string' ? data : '',
  ]
    .filter(Boolean)
    .flatMap((value) =>
      typeof value === 'string' ? value : JSON.stringify(value)
    )
    .join(' ')
    .toLowerCase();
};

const hasErrorText = (errorText, ...values) =>
  values.some((value) => errorText.includes(value));

const calibratedExclusionErrorKeys = {
  outside_price_limits: 'disclosureOutsidePriceLimitsError',
  low_confidence: 'disclosureLowConfidenceError',
  fuel_type_not_accepted: 'disclosureFuelTypeNotAcceptedError',
  model_year_not_accepted: 'disclosureModelYearNotAcceptedError',
  make_not_accepted: 'disclosureUnsupportedMakeModelError',
  vehicle_type_not_accepted: 'disclosureVehicleTypeNotAcceptedError',
  major_rust_damage: 'disclosureMajorRustDamageError',
  major_body_damage: 'disclosureMajorBodyDamageError',
  does_not_start: 'disclosureDoesNotStartError',
  high_mileage: 'disclosureHighMileageError',
  branded_title: 'disclosureBrandedTitleError',
  frame_damage: 'disclosureFrameDamageError',
  geography_not_allowed: 'disclosureGeographyNotAllowedError',
  multiple_exclusions: 'disclosureMultipleExclusionsError',
};

const vehicleDisclosureErrorCodeKeys = {
  '001': 'disclosurePartnerIdInvalidError',
  '002': 'disclosureZipInvalidError',
  '003': 'disclosureVinDecodeError',
  '004': 'disclosurePricingUnspecifiedError',
  '005': 'disclosurePricingUnspecifiedError',
  '006': 'disclosurePricingUnspecifiedError',
  '007': 'disclosurePricingUnspecifiedError',
  '010': 'disclosureAnswerInvalidError',
};

const getCalibratedExclusionErrorKey = (errorText) =>
  Object.entries(calibratedExclusionErrorKeys).find(([errorCode]) =>
    errorText.includes(errorCode)
  )?.[1];

const getPricingErrorText = (data) =>
  [
    data?.message,
    data?.error,
    data?.error_msg,
    typeof data?.detail === 'string' ? data.detail : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

/** Return the locale key for a Vehicle Disclosure API failure. */
export const getVehicleDisclosureErrorKey = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const errorCode = String(error?.response?.data?.error_code || '').padStart(
    3,
    '0'
  );
  const errorText = getErrorText(error);
  const pricingErrorText = getPricingErrorText(data);
  const partnerIdValidationError = data?.detail?.find?.(
    ({ loc }) => loc?.includes?.('partner_id')
  );
  const zipValidationError = data?.detail?.find?.(
    ({ loc }) => loc?.includes?.('zip')
  );
  const calibratedExclusionErrorKey = getCalibratedExclusionErrorKey(
    pricingErrorText
  );

  if (
    hasErrorText(
      pricingErrorText,
      'api_price_unavailable',
      'pricing_not_available'
    )
  ) {
    return 'disclosurePricingUnavailableError';
  }

  if (calibratedExclusionErrorKey) {
    return calibratedExclusionErrorKey;
  }

  if (errorCode === '008') {
    return 'disclosurePricingUnavailableError';
  }

  if (vehicleDisclosureErrorCodeKeys[errorCode]) {
    return vehicleDisclosureErrorCodeKeys[errorCode];
  }

  if (status === 400 || status === 422) {
    if (hasErrorText(errorText, 'partner_id required')) {
      return 'disclosurePartnerIdMissingError';
    }
    if (partnerIdValidationError) {
      return partnerIdValidationError.type === 'missing'
        ? 'disclosurePartnerIdMissingError'
        : 'disclosurePartnerIdInvalidError';
    }
    if (
      hasErrorText(
        errorText,
        'dealer not found for dealer_id',
        'partner not found for partner_id'
      )
    ) {
      return 'disclosurePartnerIdInvalidError';
    }
    if (hasErrorText(errorText, 'must provide zip')) {
      return 'disclosureZipMissingError';
    }
    if (hasErrorText(errorText, 'invalid zipcode', 'zipcode validation')) {
      return 'disclosureZipInvalidError';
    }
    if (zipValidationError) {
      return zipValidationError.type === 'missing'
        ? 'disclosureZipMissingError'
        : 'disclosureZipInvalidError';
    }
    if (hasErrorText(errorText, 'must provide a vin or uvc')) {
      return 'disclosureVehicleIdentifierMissingError';
    }
    if (
      hasErrorText(
        errorText,
        'odometer answer is not present',
        'odometer answer cannot be empty'
      )
    ) {
      return 'disclosureOdometerMissingError';
    }
    if (
      hasErrorText(
        errorText,
        'question type',
        'one or more answers required'
      )
    ) {
      return 'disclosureAnswerInvalidError';
    }
  }

  if (status === 500) {
    if (hasErrorText(errorText, 'pricing constraint')) {
      return 'disclosurePricingConstraintError';
    }
    if (hasErrorText(errorText, 'vin decode error')) {
      return 'disclosureVinDecodeError';
    }
    if (hasErrorText(errorText, 'pricing not available for this make and model')) {
      return 'disclosureUnsupportedMakeModelError';
    }
    return 'disclosurePricingUnspecifiedError';
  }

  if (status === 600) {
    return 'disclosurePricingUnavailableError';
  }

  return 'disclosureAddError';
};

export const getVehicleDisclosureErrorMessage = (error, locale) =>
  locale[getVehicleDisclosureErrorKey(error)] || locale.disclosureAddError;